import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { ArrowRight, CloseThin } from "~/components/Icons/fill";
import Email from "~/components/Icons/outline/Email";
import { trackEvent } from "~/helpers/tracking";
import { useRouter } from "next/router";
import InputWithAction from "~/components/InputWithAction";
import SaveQuoteStyled from "./SaveQuoteStyled";
import { REGEX_PATTERNS } from "~/constants/contact-credit";
import { SubmitHandler } from "react-hook-form";
import { theme } from "~/styles";
import { useModal } from "~/hooks/useModal";
import { ModalKeys } from "~/constants/modal";
import { useMonarchSlot } from "@redventures/cohesion-utils-react";
import { closeModal } from "~/redux/actions/modal";
import { isEmpty } from "lodash";

type SaveQuoteForm = {
  email: string;
};

interface SaveQuoteModalProps {
  visible: boolean;
  handleOnClose: () => void;
}

const SaveQuoteModal: FC<SaveQuoteModalProps> = ({ visible, handleOnClose }) => {
  const { data: monarchData, err } = useMonarchSlot<{
    modalHeadline: string;
    modalBody: string;
    modalCtaText: string;
    modalPlaceholder: string;
    linkText: string;
  }>("saveQuote");

  const { register, handleSubmit, formState } = useForm<SaveQuoteForm>({ mode: "onChange" });

  const { errors } = formState;

  const onSubmit: SubmitHandler<SaveQuoteForm> = (data) => {
    trackEvent({
      action: "elementClicked",
      data: {
        elementType: "BUTTON",
        action: "SAVE_QUOTE",
        location: currentPage,
        text: monarchData.modalCtaText,
      },
    });

    handleOnClose();
    // formSubmitted event
    trackEvent({ action: "formSubmitted", data: { formName: "EMAIL CAPTURE" } });
    // identify event
    trackEvent({ action: "identify", data: { emailAddress: data.email } });
    /**
     * customerCaptured event is delayed by 3 seconds following the identify event to
     * avoid race condition and ensure email address is properly tied to the anonymousId
     * (per Red Platform)
     */
    setTimeout(() => {
      trackEvent({ action: "customerCaptured", data: { emailAddress: data.email } });
    }, 3000);
  };

  const router = useRouter();
  const currentPage = router.route.slice(1) || "";

  const handleCloseButton = () => {
    trackEvent({
      action: "elementClicked",
      data: {
        elementType: "BUTTON",
        action: "CLOSE_MODAL",
        location: currentPage,
      },
    });

    handleOnClose();
  };

  useEffect(() => {
    if (!visible || isEmpty(monarchData) || err) {
      closeModal();
      return;
    }

    trackEvent({
      action: "elementViewed",
      data: {
        elementType: "Modal",
        location: currentPage,
        position: "Save Quote Modal",
        text: monarchData.modalHeadline,
      },
    });
  }, [visible, err, currentPage]);

  if (!visible || isEmpty(monarchData) || err) {
    closeModal();
    return null;
  }

  return (
    <SaveQuoteStyled data-testid="modal-save-quote">
      <button
        className="save-quote-modal__close-button"
        onClick={handleCloseButton}
        data-testid="modal-btn-close"
        type="button"
      >
        <CloseThin width={16} height={16} fill={theme.colors.primary.lightGray} />
      </button>

      <div className="save-quote-modal__content-container">
        <h2 className="save-quote-modal__title">{monarchData.modalHeadline}</h2>
        <p className="save-quote-modal__subtitle">{monarchData.modalBody}</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <InputWithAction
            placeholder={monarchData.modalPlaceholder}
            buttonText={monarchData.modalCtaText}
            leftIcon={<Email width={22} height={20} viewBox="0 0 22 20" />}
            inputType="email"
            variant="modal"
            testId="save-quote-form"
            ariaLabel="Email address for saving quote"
            registerProps={register("email", {
              required: "Email is required",
              pattern: {
                value: REGEX_PATTERNS.EMAIL,
                message: "Invalid email address format",
              },
            })}
            error={errors.email?.message}
            id="save-quote-email"
            autoComplete="email"
          />
        </form>
      </div>
    </SaveQuoteStyled>
  );
};

SaveQuoteModal.displayName = "SaveQuoteModal";

export default SaveQuoteModal;

export const SaveQuoteButton: FC = () => {
  const { openModal } = useModal();
  const { data: monarchData } = useMonarchSlot<{
    linkText: string;
  }>("saveQuote");

  if (isEmpty(monarchData)) return null;

  return (
    <button
      onClick={() => openModal(ModalKeys.SAVE_QUOTE)}
      style={{
        padding: 0,
        margin: 0,
        background: "none",
        border: "none",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "16px",
        textDecoration: "underline",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: "16px", fontWeight: "700", lineHeight: "16px" }}>{monarchData.linkText}</span>{" "}
      <ArrowRight width={16} height={16} />
    </button>
  );
};

SaveQuoteButton.displayName = "SaveQuoteButton";
